import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { Auth } from 'src/providers/auth/auth';
import { Me } from 'src/providers/me/me';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Client } from 'src/providers/client/client';
import { Events } from 'src/providers/events/events';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { OnboardingStepNavigationService } from 'src/shared/services/onboarding-step-navigation.service';

@Component({
  selector: 'app-login',
  template: '<div></div>',
  standalone: false
})
export class LoginPage implements OnInit {
  constructor(
    private auth: Auth,
    private authModalService: AuthModalService,
    private client: Client,
    private events: Events,
    private me: Me,
    private navCtrl: CustomNavController,
    private queryClient: QueryClient,
    private route: ActivatedRoute,
    private router: Router,
    private storage: TidyStorage,
    private stepNavigation: OnboardingStepNavigationService,
    private util: Util
  ) {}

  async ngOnInit() {
    const token = this.route.snapshot.paramMap.get('token');
    // A `?next=subscribe` deep link (generated by concierge for a client) drops the user
    // straight onto the Subscribe step once the token login completes.
    const next = this.route.snapshot.queryParamMap.get('next');

    if (token) {
      // Suppress the global logout-on-401 while we swap sessions (see Auth.beginTokenLogin): a
      // stray 401 from the OUTGOING session — clearing it or an in-flight bootstrap request —
      // must not wipe the new session and bounce the user to the auth modal. app.component
      // already opened this window at bootstrap for the /login URL; this covers direct
      // navigations into the route.
      this.auth.beginTokenLogin();
      try {
        this.authModalService.setTokenLoginLoading(true);
        await this.clearPreviousSessionData();
        await new Promise(resolve => setTimeout(resolve, 200));
        await this.storage.save('didJustSignIn', true);
        await this.auth.login({ auth_token: token }, 'login', null);
        const account = await this.me.load(true);
        const customerObj: any = account;
        this.auth.setAccountTypeStorage(customerObj.customer_account.account_type);
        this.auth.setSession();
        await this.client.getAddresses(true);
        await this.storage.save('isGuestAccount', false);
        this.authModalService.setTokenLoginLoading(false);
        await this.authModalService.dismissAuthModal();
        this.events.publish('auth:complete');
        await this.navCtrl.navigateForward('properties');
        // A `?next=subscribe` link lands the user on the Subscribe step right after login:
        // the right-side "Subscribe" dialog on desktop, or the full Subscribe page on mobile
        // — the exact flow the "Subscribe" onboarding item opens (Enable Autopilot page).
        if (next === 'subscribe') {
          await this.openSubscribeStep();
        }
        // Keep suppression active for a short grace period so a late-resolving 401 from the
        // outgoing session doesn't tear down the new one just after we land on /overview.
        this.auth.endTokenLogin();
      } catch (err: any) {
        this.auth.endTokenLogin();
        this.authModalService.setTokenLoginLoading(false);
        // HttpClientCustom reshapes API errors to { code, message, err }, so check `code` too —
        // `status` is only present on raw HttpErrorResponses.
        if (err?.status === 404 || err?.status === 401 || err?.code === 404 || err?.code === 401) {
          this.util.showError('This login link is expired or invalid. Please try logging in again.');
        } else {
          this.util.showError('Failed to log in. Please try again.');
        }
        // clearPreviousSessionData already dropped any prior session, so a failed link leaves the
        // user logged out. Route to the plain `login` page (openSignIn), where DashboardPage
        // surfaces the sign-in modal for recovery — `overview` would instead open the guest-type
        // modal, and opening one here ourselves would race that. (Previously the 401→logout+reload
        // surfaced a modal; we now suppress that reload, so we steer to the sign-in entry point.)
        this.navCtrl.navigateForward('login');
      }
    } else {
      this.navCtrl.navigateForward('properties');
    }
  }

  // Opens the "Subscribe" onboarding step (Enable Autopilot page) via the shared step
  // navigation, so the subscribe link behaves exactly like tapping the item on the Get
  // Started page: right-side panel on desktop, full page on mobile, and — on submit — the
  // account-wide subscription plus the client-locked "subscribe" onboarding checkmarks.
  private async openSubscribeStep(): Promise<void> {
    // Let the overview route + right-side dialog host settle before opening the panel.
    await new Promise(resolve => setTimeout(resolve, 300));
    let addressId = 0;
    try {
      const addresses = await this.client.getAddresses();
      addressId = addresses?.[0]?.id ?? 0;
    } catch {
      addressId = 0;
    }
    // The subscribe step is address-agnostic (subscribing is account-wide and marks the
    // step done for every address), so the first address is a safe anchor for the params.
    await this.stepNavigation.openStep('subscribe', addressId);
  }

  private async clearPreviousSessionData(): Promise<void> {
    // Drop the in-memory TanStack Query cache (addresses, listing settings, etc.) whenever we
    // swap sessions. It's an app-root singleton that localStorage/TidyStorage clears never touch,
    // so without this a login-as-client keeps serving the PREVIOUS account's cached data until a
    // hard refresh reboots the app — e.g. the optimizer hydrating stale addresses on first visit.
    this.queryClient.clear();
    if (!localStorage.getItem('tidy_token')) {
      return;
    }
    // Preserve UUID for analytics session continuity (used as session_token)
    const uuid = localStorage.getItem('uuid');
    await this.storage.clear();
    if (uuid !== null) {
      localStorage.setItem('uuid', uuid);
    }
  }
}

